import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Global from '../../../../Services/Common/Global';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { RIS_CUSTOM_Schema } from './RIS_CUSTOM_Schema';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';

/**
 * GetRisCustomR1 응답: { Data: [{ RiskMasterList: GetRisCustomR1Item[], RiskRule }] }
 * GetRisCustomR1Item: RsCd, RsNm, RsGubun, RsMethod, RsScale, DepNm, 날짜, RsMemberNm, MatrixList[]
 */
const mapRisCustomR1ToGridRows = (response) => {
  const raw = response ?? {};
  const first = raw?.Data?.[0] ?? raw?.data?.[0] ?? {};
  const list =
    first?.RiskMasterList ??
    first?.riskMasterList ??
    raw?.RiskMasterList ??
    raw?.riskMasterList ??
    [];
  return list.map((item) => {
    const matrixList = item.MatrixList ?? item.matrixList ?? [];
    return {
      rsCd: item.RsCd ?? item.rsCd,
      rsNm: item.RsNm ?? item.rsNm ?? '',
      rsGubun: item.RsGubun ?? item.rsGubun ?? '',
      rsMethod: item.RsMethod ?? item.rsMethod ?? '',
      rsScale: item.RsScale ?? item.rsScale ?? '',
      depNm: item.DepNm ?? item.depNm ?? '',
      rsStartDate: item.RsStartDate ?? item.rsStartDate ?? '',
      rsEndDate: item.RsEndDate ?? item.rsEndDate ?? '',
      rsMember: item.RsMemberNm ?? item.rsMemberNm ?? item.rsMember ?? '',
      matrixList: Array.isArray(matrixList) ? matrixList : [],
    };
  });
};

/** 규정 화면 등에서 RiskRule 단건 참조용 */
export const extractRisCustomR1RiskRule = (response) => {
  const raw = response ?? {};
  const first = raw?.Data?.[0] ?? raw?.data?.[0] ?? {};
  return first?.RiskRule ?? first?.riskRule ?? null;
};

/**
 * 위험성평가 메인그리드 조회 (GetRisCustomR1)
 * CustCd, Year, SearchText 암호화 전송 → 응답 RiskMasterList를 그리드 행으로 변환
 */
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(RIS_CUSTOM_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['RiskEvaluation', 'getRisCustomR1', params],
    queryFn: async () => {
      const raw = await apiRequest(
        'post',
        Global.urls.RiskEvaluation.getRisCustomR1,
        params
      );
      return raw;
    },
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess || !query.data) return;

    const riskRule = extractRisCustomR1RiskRule(query.data);
    const rows = mapRisCustomR1ToGridRows(query.data);
    handleQuerySuccess(rows, {
      onSuccess: async (processedRows) => {
        await onSuccess?.(processedRows, riskRule);
      },
    });
    // query.data 참조 시 무한 루프 가능하므로 dataUpdatedAt만 의존
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * 사업장의 실시규정 저장 (SaveRisCustomU2)
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(
      rows,
      RIS_CUSTOM_Schema.mapper.saveRisCustomU2
    );

    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisCustomU2,
      payload.length === 1 ? payload[0] : payload
    );
  }, options);
};

/**
 * 위험성평가 커스텀 마스터 삭제 (DeleteRisCustomD1)
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(
      rows,
      RIS_CUSTOM_Schema.mapper.delete1
    ).filter((p) => Number(p?.RsCd) > 0);

    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.deleteRisCustomD1,
      payload
    );
  }, options);
};
